
import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  PauseCircleIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react'
import { Badge } from '@/components/ui/badge';
import { cn } from "@/lib/utils";

// Using string literals to make it more flexible with imports from different modules
export type StatusType = 
  | 'ACTIVE'
  | 'COMPLETED'
  | 'ON_HOLD'
  | 'CANCELED'
  | 'PENDING'
  | 'INACTIVE'
  | string;

interface StatusConfig {
  icon: ReactNode;
  colors: string;
  label?: string;
}

const statusConfig: Record<string, StatusConfig> = {
  ACTIVE: { 
    icon: <CheckCircleIcon className="h-3 w-3" />, 
    colors: 'bg-emerald-50 text-emerald-600 border-emerald-200/60 hover:bg-emerald-100/80 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/30',
    label: 'Active'
  },
  INACTIVE: {
    icon: <XCircleIcon className="h-3 w-3" />,
    colors: 'bg-slate-50 text-slate-500 border-slate-200/60 hover:bg-slate-100/80 dark:bg-slate-900/20 dark:text-slate-400 dark:border-slate-800 dark:hover:bg-slate-900/30',
    label: 'Inactive'
  },
  COMPLETED: { 
    icon: <CheckCircleIcon className="h-3 w-3" />, 
    colors: 'bg-emerald-50 text-emerald-600 border-emerald-200/60 hover:bg-emerald-100/80 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/30',
    label: 'Completed'
  },
  ON_HOLD: { 
    icon: <PauseCircleIcon className="h-3 w-3" />, 
    colors: 'bg-amber-50 text-amber-600 border-amber-200/60 hover:bg-amber-100/80 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/30',
    label: 'On Hold'
  },
  CANCELED: { 
    icon: <XCircleIcon className="h-3 w-3" />, 
    colors: 'bg-slate-100 text-slate-500 border-slate-200/60 hover:bg-slate-200/80 dark:bg-slate-900/20 dark:text-slate-400 dark:border-slate-800 dark:hover:bg-slate-900/30',
    label: 'Canceled'
  },
  PENDING: { 
    icon: <ClockIcon className="h-3 w-3" />, 
    colors: 'bg-sky-50 text-sky-600 border-sky-200/60 hover:bg-sky-100/80 dark:bg-sky-900/20 dark:text-sky-400 dark:border-sky-800 dark:hover:bg-sky-900/30',
    label: 'Pending'
  }
};

export interface StatusBadgeProps {
  status: StatusType;
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline';
}

// Utility to humanize raw status ids/slugs to title case
const humanize = (val: string) => {
  if (!val) return '';
  const s = val.replace(/[_-]+/g, ' ').toLowerCase();
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
};

export function StatusBadge({ 
  status, 
  showIcon = true,
  showLabel = true, 
  className = "",
  size = 'md',
  variant = 'outline'
}: StatusBadgeProps) {
  // Normalize status to uppercase for config lookup
  const normalizedStatus = status?.toUpperCase() || 'UNKNOWN';
  
  // Get configuration or use a default
  const config = statusConfig[normalizedStatus] || {
    icon: <WarningCircleIcon className="h-3.5 w-3.5" />,
    colors: 'bg-muted text-muted-foreground border-muted hover:bg-muted/90',
    label: normalizedStatus
  };

  // Determine size classes
  const sizeClasses = {
    sm: 'h-6 text-sm px-2 py-0.5',
    md: 'h-7 text-sm px-2.5 py-1',
    lg: 'h-8 text-sm px-3 py-1.5',
  }[size];
  
  // Display label: prefer config label; otherwise, humanize the raw id
  const displayLabel = config.label || humanize(String(status || normalizedStatus));
  
  return (
    <Badge 
      variant={variant} 
      className={cn(
        "font-normal gap-1 rounded-full flex items-center border transition-colors", 
        config.colors, 
        sizeClasses, 
        className
      )}
    >
      {showIcon && config.icon}
      {showLabel && <span className="capitalize">{displayLabel}</span>}
    </Badge>
  );
} 
